/**
 * @expFlag auth-mobile-redirect-app-main-actions
 * @ticket CLOUDWEB-20237
 * @file
 */

import { Button, ButtonGroup, Spacing, Subhead, Title } from '@vkontakte/vkui';
import modalImage from 'img/allFunctionsInApp/cloud_functions.png';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import type { IPropsWithPopup } from 'reactApp/modules/popup/popup.helpers';
import { sendRedirectToAppAnalytics } from 'reactApp/modules/redirectToApp/redirectToApp.analytics';
import { createDwhDataForRedirectToApp, setRedirectAppMainActionsShown } from 'reactApp/modules/redirectToApp/redirectToApp.helpers';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { ECategoryGa } from 'reactApp/utils/paymentGa';

import styles from './AllFunctionsInAppModal.css';
import type { AllFunctionsInAppModalProps } from './AllFunctionsInAppModal.types';

export const AllFunctionsInAppModal = memo<IPropsWithPopup<AllFunctionsInAppModalProps>>(
    ({ onClose, href, actionType, buttonLocation, routeId, storage }) => {
        const ref = useRef<HTMLDivElement>(null);

        useEffect(() => {
            sendRedirectToAppAnalytics(
                createDwhDataForRedirectToApp({
                    actionType,
                    buttonLocation,
                    routeId,
                    storage,
                    action: 'show',
                    eventCategory: ECategoryGa.modalActionBlock,
                })
            );
        }, []);

        const handleClose = useCallback(() => {
            setRedirectAppMainActionsShown(actionType);
            sendRedirectToAppAnalytics(
                createDwhDataForRedirectToApp({
                    actionType,
                    buttonLocation,
                    routeId,
                    storage,
                    action: 'close',
                    eventCategory: ECategoryGa.modalActionBlock,
                })
            );
            onClose();
        }, [actionType, buttonLocation, onClose, routeId, storage]);

        const handleClick = useCallback(() => {
            setRedirectAppMainActionsShown(actionType);
            sendRedirectToAppAnalytics(
                createDwhDataForRedirectToApp({
                    actionType,
                    buttonLocation,
                    routeId,
                    storage,
                    action: 'click_install',
                    eventCategory: ECategoryGa.modalActionBlock,
                })
            );
            onClose();
        }, [actionType, buttonLocation, onClose, routeId, storage]);

        const title = 'Все функции — в приложении';
        const subtitle = 'В нём будут все ваши файлы и больше возможностей — сможете навести порядок в фото и документах';
        const closeButtonText = 'Остаться';
        const actionButtonText = 'Продолжить';

        return (
            <MobileDialog
                id="all-functions-in-app-modal"
                onClose={handleClose}
                mod="base"
                topmost
                scrollRef={ref}
                contextPadding="zero"
                closable={false}
            >
                <div className={styles.root} ref={ref}>
                    <img src={modalImage} alt="Облако для телефона" data-qa-id="all-functions-in-app-modal-img" className={styles.image} />
                    <div className={styles.mainContent}>
                        <div className={styles.textContent}>
                            <Title level="2" data-qa-id="all-functions-in-app-modal-header">
                                {title}
                            </Title>
                            <Subhead weight="3" className={styles.subhead} data-qa-id="all-functions-in-app-modal-content">
                                {subtitle}
                            </Subhead>
                        </div>
                        <Spacing size={24} />
                        <ButtonGroup mode="vertical" gap="m" stretched>
                            <Button
                                size="l"
                                mode="primary"
                                appearance="neutral"
                                onClick={() => handleClose()}
                                stretched
                                Component="button"
                                data-qa-id="all-functions-in-app-modal-button-close"
                            >
                                {closeButtonText}
                            </Button>
                            <Button
                                size="l"
                                mode="primary"
                                appearance="accent"
                                onClick={() => handleClick()}
                                stretched
                                Component="a"
                                href={href}
                                data-qa-id="all-functions-in-app-modal-button-action"
                            >
                                {actionButtonText}
                            </Button>
                        </ButtonGroup>
                    </div>
                </div>
            </MobileDialog>
        );
    }
);

AllFunctionsInAppModal.displayName = 'AllFunctionsInAppModal';
